import { useState, useEffect } from 'react';
import useAuth from '@sdk/authentication/useAuth';
import validateTime from '@sdk/utils/validation/validateTime';

import { UseBreakRowProps } from '@web/timesheets/CreateTimesheetModal/components/rows/BreakRow/breakRow.types';
import {
  getRowLabel,
  pickEndBreakRangeStart,
} from '@web/timesheets/CreateTimesheetModal/components/rows/BreakRow/breakRow.utils';
import { DEFAULT_INTERVAL_IN_MINUTES_FOR_TIME_PICKERS } from '@web/timeClock/constants';

export const useBreakRow = ({
  breakValue,
  fieldError,
  isLastEntry,
  submitBreakError,
  update,
  timeClockFieldSet,
}: UseBreakRowProps) => {
  const { currentUser } = useAuth();
  const { usTimeFormat, worldClockEnabled } = currentUser.settings;
  const rowLabel = getRowLabel(breakValue.type);
  const { value: clockIn } = timeClockFieldSet.clockIn;
  const { value: clockOut } = timeClockFieldSet.clockOut;
  const { isStartInvalid, isEndInvalid } = useBreakRowValidation({
    fieldError,
    isLastEntry,
    submitBreakError,
    clockIn,
    clockOut,
    breakStart: breakValue.start,
    breakEnd: breakValue.end,
  });

  // Limit the TimePicker options to start with this hour for start break field
  const [startBreakStartHour, setStartBreakStartHour] = useState(undefined);
  // Limit the TimePicker options to end with this hour for start break field
  const [startBreakEndHour, setStartBreakEndHour] = useState(undefined);
  // Limit the TimePicker options to start with this hour for end break field
  const [endBreakStartHour, setEndBreakStartHour] = useState(undefined);
  // Limit the TimePicker options to end with this hour for end break field
  const [endBreakEndHour, setEndBreakEndHour] = useState(undefined);
  useTimePickerRangeAdjustment({
    clockIn,
    clockOut,
    breakStart: breakValue.start,
    breakEnd: breakValue.end,
    setStartBreakStartHour,
    setStartBreakEndHour,
    setEndBreakStartHour,
    setEndBreakEndHour,
  });

  const onStartBreakChange = (value: string) => {
    update({
      ...breakValue,
      start: value,
    });

    // After each update to start of the break adjust the picker options for end break TimePicker
    if (validateTime(value)) {
      if (validateTime(clockIn)) {
        setEndBreakStartHour(clockIn > value ? clockIn : value);
      } else {
        setEndBreakStartHour(value);
      }
    }
  };

  const onEndBreakChange = (value: string) => {
    update({
      ...breakValue,
      end: value,
    });
  };

  return {
    rowLabel,
    onStartBreakChange,
    usTimeFormat,
    worldClockEnabled,
    onEndBreakChange,
    isStartInvalid,
    isEndInvalid,
    startBreakStartHour,
    startBreakEndHour,
    endBreakStartHour,
    endBreakEndHour,
  };
};

const useBreakRowValidation = ({
  fieldError,
  isLastEntry,
  submitBreakError,
  breakStart,
  breakEnd,
  clockIn,
  clockOut,
}) => {
  const [isStartInvalid, setIsStartInvalid] = useState(false);
  const [isEndInvalid, setIsEndInvalid] = useState(false);

  useEffect(() => {
    if (fieldError?.length) {
      const hasClockInAndClockOut = Boolean(clockIn && clockOut);
      setIsStartInvalid(!breakStart.length);

      if (!isLastEntry || hasClockInAndClockOut) {
        setIsEndInvalid(!breakEnd?.length);
      } else {
        setIsEndInvalid(false);
      }
    } else {
      setIsStartInvalid(false);
      setIsEndInvalid(false);
    }

    if (submitBreakError?.length) {
      setIsStartInvalid(true);
      setIsEndInvalid(true);
    }
  }, [fieldError, isLastEntry, submitBreakError, clockIn, clockOut, breakStart, breakEnd]);

  return {
    isStartInvalid,
    isEndInvalid,
  };
};

const useTimePickerRangeAdjustment = ({
  clockIn,
  clockOut,
  breakStart,
  breakEnd,
  setStartBreakStartHour,
  setStartBreakEndHour,
  setEndBreakStartHour,
  setEndBreakEndHour,
}) => {
  useEffect(() => {
    if (validateTime(clockOut)) {
      setStartBreakEndHour(clockOut);
      setEndBreakEndHour(clockOut);
    }

    // Adjust the TimePicker options to match the time range set by clock in and clock out.
    const endBreakRangeStart = pickEndBreakRangeStart({
      clockIn: validateTime(clockIn) ? clockIn : undefined,
      clockOut: validateTime(clockOut) ? clockOut : undefined,
      startBreak: validateTime(breakStart) ? breakStart : undefined,
      endBreak: validateTime(breakEnd) ? breakEnd : undefined,
      intervalInMinutes: DEFAULT_INTERVAL_IN_MINUTES_FOR_TIME_PICKERS,
    });
    setEndBreakStartHour(endBreakRangeStart);

    if (validateTime(breakStart)) {
      setStartBreakStartHour(breakStart);
    } else if (validateTime(clockIn)) {
      setStartBreakStartHour(clockIn);
    }
  }, [clockIn, clockOut, breakStart, breakEnd]);
};
